<template>
  <div>
    <h5
      v-if="!isBooking"
      class="add_bottom_15"
    >
      {{ $t('lang.lockers.select_locker') }}
    </h5>

    <div
      v-for="locker in lockers"
      :key="locker.id"
    >
      <div
        v-if="allowModification || quantities[locker.id]"
        class="strip map_view add_top_20"
        style="min-height: 80px"
      >
        <div
          class="row no-gutters"
          style="cursor: pointer"
        >
          <div class="col-5">
            <figure
              class="dark-background"
              style="min-height: 80px; max-height: 150px"
            >
              <small class="locker-name">{{ locker.name }}</small>
              <img
                :src="getLockerImage(locker)"
                class="img-cover"
                alt="Locker"
                @click="openViewer(getLockerImage(locker))"
              >
            </figure>
          </div>

          <div class="col-7 border-left">
            <div
              class="wrapper pt-2"
              style="min-height: 80px"
            >
              <div>
                <p class="text">
                  <b v-if="showInCms">
                    {{ $t('lang.lockers.dimensions_cm', {
                      width: parseInt(locker.width),
                      height: parseInt(locker.height),
                      depth: parseInt(locker.depth)
                    }) }}
                  </b>
                  <b v-else>
                    {{ $t('lang.lockers.dimensions_in', {
                      width: parseInt(locker.width / 2.54),
                      height: parseInt(locker.height / 2.54),
                      depth: parseInt(locker.depth / 2.54)
                    }) }}
                  </b>
                &nbsp;&nbsp;
                  <a
                    class="color_text small"
                    @click="showInCms = !showInCms"
                  > <i class="fas fa-exchange" /></a>
                </p>
                <p class="text small">
                  {{ locker.description }}
                </p>

                <!-- Selector de cantidad -->
                <div class="d-flex justify-content-between align-items-center mt-2 mt-lg-4">
                  <h6 class="m-0 font-weight-bold">
                    <span v-if="useHourly">
                      ${{ formatedPrice(locker.price_lists[0].price_per_hour) }} /
                      <small> {{ $tc('lang.lockers.price_per_hour', 1) }}</small>
                    </span>
                    <span v-else>
                      ${{ formatedPrice(locker.price_lists[0].price_per_day) }}  /
                      <small> {{ $tc('lang.lockers.price_per_day', 1) }} </small>
                    </span>
                  </h6>
                  <div
                    v-if="allowModification"
                    class="quantity-wrapper"
                  >
                    <button
                      class="qty-btn left"
                      type="button"
                      @click="decrement(locker.id)"
                    >
                      <i class="fas fa-minus" />
                    </button>

                    <input
                      type="number"
                      class="qty-input"
                      :value="quantities[locker.id] || 0"
                      readonly
                    >

                    <button
                      class="qty-btn right"
                      type="button"
                      :disabled="isIncrementDisabled(locker.id)"
                      @click="increment(locker.id)"
                    >
                      <i class="fas fa-plus" />
                    </button>
                  </div>
                  <div v-else>
                    <p><b>{{ $t('lang.lockers.quantity') }}: </b>{{ quantities[locker.id] }}</p>
                  </div>
                </div>
              </div>

            <!-- Fin Selector de cantidad -->
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      id="total-price"
      class="mt-4 text-right"
    >
      <h6 class="mb-0">
        {{ $t('lang.lockers.total') }}:
        <span
          v-if="isBooking && booking.discount_code "
          class="font-weight-light ml-1 mr-1"
          style="text-decoration: line-through;"
        >
          {{ formatedPrice(
            Number(booking.total_price || 0) + Number(booking.discount_amount || 0)
          ) }}
          {{ lockers[0].price_lists[0].currency }}
        </span>

        <strong>{{ formatedPrice(totalPrice) }} {{ lockers[0].price_lists[0].currency }} </strong>
      </h6>

      <p
        class="mt-1 small text-muted"
        v-html="translatedPriceDescription"
      />
    </div>

    <div v-if="canShowLockerAccessCodes">
      <p
        class="text-center small"
        style="margin-bottom: 3px"
      >
        <b-link
          class="btn btn-locker-codes btn_1 full-width "
          type="button"
          @click="openModalLockerCodes"
        >
          <i class="fas fa-key mr-1" />
          {{ $t('lang.lockers.see_access_codes') }}
        </b-link>
      </p>
    </div>
    <hr v-if="!isBooking">

    <ImageViewer
      :id="'locker-image-viewer'"
      :image-url="viewerImage"
    />
    <LockerAccessCodeModal
      :access-codes="lockerAccessCodes"
      :is-loading="lockerAccessCodesLoading"
      :is-not-available-yet="lockerAccessCodesNotAvailableYet"
    />
  </div>
</template>

<script>
import moment from 'moment';
import ImageViewer from '@/components/ImageViewer.vue';
import LockerAccessCodeModal from '@/components/Locker/LockerAccessCodeModal.vue';
import { API } from '@/api/api';

const BookingAPI = API.get('booking');

export default {
  name: 'LockerOptions',
  components: { LockerAccessCodeModal, ImageViewer },

  props: {
    lockers: {
      type: Array,
      required: true,
    },
    searchParams: {
      type: Object,
      required: false,
      default: null,
    },
    booking: {
      type: Object,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      quantities: {},
      useHourly: true,
      hours: 0,
      days: 0,
      showInCms: true,
      isBooking: false,
      allowModification: true,
      viewerImage: null,
      lockerAccessCodes: [],
      lockerAccessCodesLoading: false,
      lockerAccessCodesNotAvailableYet: false,
    };
  },

  computed: {
    canShowLockerAccessCodes() {
      return this.booking
        && ['confirmed', 'at_store'].includes(this.booking.booking_status);
    },
    totalPrice() {
      if (this.isBooking) {
        return this.booking.total_price;
      }
      return this.lockers.reduce((total, locker) => {
        const qty = this.quantities[locker.id] || 0;
        const price = locker.price_lists[0];
        const unit = this.useHourly
          ? price.price_per_hour * this.hours
          : price.price_per_day * this.days;
        return total + qty * unit;
      }, 0).toFixed(2);
    },
    translatedPriceDescription() {
      const duration = this.useHourly
        ? `${this.hours} ${this.$tc('lang.lockers.price_per_hour', this.hours)}`
        : `${this.days} ${this.$tc('lang.lockers.price_per_day', this.days)}`;

      return this.$t('lang.lockers.price_description', { duration });
    },
  },

  created() {
    if (this.booking) {
      this.isBooking = true;
      if (this.booking.booking_status !== 'pending') {
        this.allowModification = false;
      }
    }
    this.loadQuantities();
    this.calculateBestRate();
  },

  mounted() {
    if (this.booking && this.booking.booking_status === 'pending') {
      this.$scrollTo('#total-price', { offset: -130 });
    }
  },

  methods: {
    formatedPrice(price) {
      const value = Number(price);

      return value.toLocaleString('de-DE', {
        minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
        maximumFractionDigits: 2,
      });
    },
    getLockerImage(locker) {
      const path = `${process.env.VUE_APP_BACKEND_SERVER}/storage/${locker.image_path}`;
      return encodeURI(path).replace(' ', '%20');
    },

    calculateBestRate() {
      const smallestLocker = this.lockers[0].price_lists[0];

      let start;
      let end;
      if (this.isBooking) {
        start = moment(this.booking.start_date);
        end = moment(this.booking.end_date);
      } else {
        start = moment(this.searchParams.input_start_date);
        end = moment(this.searchParams.input_end_date);
      }

      // Horas totales - redondeo para arriba
      const durationInHours = Math.ceil(moment.duration(end.diff(start)).asHours());
      const durationInDays = end.startOf('day').diff(start.startOf('day'), 'days') + 1;

      const hourlyPrice = smallestLocker.price_per_hour * durationInHours;
      const dailyPrice = smallestLocker.price_per_day;

      this.useHourly = hourlyPrice < dailyPrice;
      this.hours = durationInHours;
      this.days = durationInDays;
    },
    loadQuantities() {
      if (this.booking && this.booking.booking_items) {
        const lockers = this.booking.store.lockers || [];

        this.booking.booking_items.forEach((item) => {
          const matchingLocker = lockers.find((locker) =>
            locker.price_lists[0] && locker.price_lists[0].id === item.locker_price_list_id);

          if (matchingLocker) {
            this.$set(this.quantities, matchingLocker.id, item.quantity);
          }
        });
      }

      this.emitSelection();
    },
    getMaxAvailable(locker) {
      if (!this.booking || !this.booking.lockers_availability) {
        return null;
      }

      if (this.booking.lockers_availability[locker] === undefined) {
        return null;
      }

      return Number(this.booking.lockers_availability[locker]);
    },
    isIncrementDisabled(locker) {
      const maxAvailable = this.getMaxAvailable(locker);

      if (maxAvailable === null) {
        return false;
      }

      const current = this.quantities[locker] || 0;
      return current >= maxAvailable;
    },
    increment(locker) {
      if (this.isIncrementDisabled(locker)) {
        return;
      }

      const current = this.quantities[locker] || 0;
      this.$set(this.quantities, locker, current + 1);
      this.emitSelection();
    },

    decrement(locker) {
      const current = this.quantities[locker] || 0;
      if (current > 0) {
        const totalSelected = Object.values(this.quantities)
          .reduce((total, quantity) => total + (quantity || 0), 0);

        if (this.booking && totalSelected > 1) {
          this.$set(this.quantities, locker, current - 1);
          this.emitSelection();
        } else if (!this.booking && totalSelected >= 0) {
          this.$set(this.quantities, locker, current - 1);
          this.emitSelection();
        }
      }
    },

    emitSelection() {
      const selection = Object.entries(this.quantities)
        .filter(([, quantity]) => quantity > 0)
        .map(([id, quantity]) => ({ id: Number(id), quantity }));

      this.$emit('lockerSelectionChanged', selection);
    },

    openViewer(lockerImage) {
      this.viewerImage = lockerImage;
      this.$bvModal.show('locker-image-viewer');
    },

    isBookingStartDateToday() {
      return moment().isSameOrAfter(moment(this.booking.start_date), 'day');
    },

    async loadLockerAccessCodes() {
      if (!this.booking || !this.booking.id) {
        return;
      }

      this.lockerAccessCodesLoading = true;

      try {
        const { data } = await BookingAPI.getLockerBookingCodes(this.booking.id);
        const codes = Array.isArray(data) ? data : [];
        this.lockerAccessCodes = codes.filter((code) => code && code.qr_content);
      } catch (error) {
        this.lockerAccessCodes = [];
      } finally {
        this.lockerAccessCodesLoading = false;
      }
    },

    openModalLockerCodes() {
      this.lockerAccessCodes = [];
      this.lockerAccessCodesLoading = false;
      this.lockerAccessCodesNotAvailableYet = !this.isBookingStartDateToday();
      this.$bvModal.show('locker-code-modal');

      if (this.lockerAccessCodesNotAvailableYet) {
        return;
      }

      this.loadLockerAccessCodes();
    },

  },
};
</script>

<style scoped>
.quantity-wrapper {
  position: relative;
  display: inline-block;
  width: 90px;
  margin-bottom: 10px;
}

.qty-input {
  width: 100%;
  height: 33px;
  text-align: center;
  padding: 0 28px;
  border: 1px solid #ced4da;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  background-color: #fff;
}

.qty-btn {
  position: absolute;
  top: 0;
  width: 28px;
  height: 100%;
  background: none;
  border: none;
  color: #555;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.qty-btn.left {
  left: 0;
}

.qty-btn.right {
  right: 0;
}

.qty-btn:hover {
  background-color: #f1f1f1;
  border-radius: 50%;
}

.qty-btn:disabled {
  color: #bbb;
  cursor: not-allowed;
}

.qty-btn:disabled:hover {
  background-color: transparent;
}

p.text{
  line-height: 1.2;
  margin: .5rem 0 0 0;
}

.locker-name{
  right: 10px !important;
  top: 10px !important;
  font-size: 1.2rem;
}

.btn-locker-codes{
  padding: 10px;
  font-size: 0.875rem;
}

</style>
